/**
 * Діалог створення/редагування кастомного бізнес-шаблону
 * (Порція Виробництво.5, Етап 3).
 *
 * Дзеркало RoleEditorDialog для access_roles. Створення = майстер у 2
 * кроки (вибір джерела → редагування модулів/документів); редагування =
 * лише крок 2.
 *
 * Перед PATCH'ем зі звуженим enabled_modules — викликаємо
 * GET /business-templates/{id}/affected-docs?modules=... і показуємо
 * ConfirmModuleNarrowingModal з підрахунком документів.
 *
 * Системні шаблони — read-only (403 від backend; UI ховає кнопки).
 */

import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  businessTemplates,
  type BusinessTemplate,
  type AffectedDocs,
} from '@/api/admin';
import { humanizeBackendError } from '@/utils/backendError';
import { moduleName, MODULE_NAMES_UK } from '@/utils/moduleNames';
import { ConfirmModuleNarrowingModal } from '@/components/ConfirmModuleNarrowingModal';

interface Props {
  /** null = створення; BusinessTemplate = редагування. */
  template: BusinessTemplate | null;
  /** Усі видимі шаблони (системні + custom) — для вибору джерела при створенні. */
  allTemplates: BusinessTemplate[];
  onClose: () => void;
  onSaved: () => void;
}

// Доступні модулі у формі — ті, що відомі UI (sidebar). Узгоджено з
// ALL_AVAILABLE_MODULES у useEnabledModules.ts. Тут масив фіксованого
// порядку для стабільного відображення.
const FORM_MODULES: string[] = [
  'refs', 'sales', 'purchases', 'warehouse',
  'cash', 'bank', 'accounting', 'tax',
  'payroll', 'production', 'reports', 'assets',
  'retail', 'settlements',  // були відсутні у формі — додано 2026-06-07
  'returnable_packaging',  // Облік тари (Порція C, 2026-06-07)
];

export default function BusinessTemplateEditorDialog({
  template,
  allTemplates,
  onClose,
  onSaved,
}: Props) {
  const isEdit = template !== null;

  // Step 1 — для створення лише.
  const [step, setStep] = useState<1 | 2>(isEdit ? 2 : 1);
  const [sourceId, setSourceId] = useState<string>('');

  // Поля редагування.
  const [name, setName] = useState<string>(isEdit ? template!.name : '');
  const [description, setDescription] = useState<string>(
    isEdit ? template!.description ?? '' : ''
  );
  const [enabledModules, setEnabledModules] = useState<Set<string>>(
    isEdit ? new Set(template!.enabled_modules) : new Set()
  );

  const [error, setError] = useState<string | null>(null);

  // Confirmation modal для звуження модулів.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [affected, setAffected] = useState<AffectedDocs | null>(null);
  const [removedModulesPreview, setRemovedModulesPreview] = useState<string[]>([]);
  const affectedQuery = useQuery({
    queryKey: ['affected-docs', template?.id, Array.from(removedModulesPreview).sort().join(',')],
    queryFn: () =>
      businessTemplates.affectedDocs(template!.id, removedModulesPreview),
    enabled: confirmOpen && isEdit && removedModulesPreview.length > 0,
    staleTime: 30 * 1000,
  });
  useEffect(() => {
    if (affectedQuery.data) setAffected(affectedQuery.data);
  }, [affectedQuery.data]);

  // Тільки видимі шаблони (системні + свого tenant'а — backend RLS).
  // Сортуємо: системні зверху.
  const availableSources = useMemo(
    () => [...allTemplates].sort((a, b) => {
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
      return a.name.localeCompare(b.name);
    }),
    [allTemplates]
  );

  // При зміні source при створенні — preset modules та name-placeholder.
  useEffect(() => {
    if (!isEdit && sourceId) {
      const src = allTemplates.find((t) => t.id === sourceId);
      if (src) {
        setEnabledModules(new Set(src.enabled_modules));
        if (!name) setName(`${src.name} (копія)`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, allTemplates, isEdit]);

  // Original modules — для виявлення звуження при редагуванні.
  const originalModules = useMemo(
    () => (isEdit ? new Set(template!.enabled_modules) : new Set<string>()),
    [isEdit, template]
  );

  const removedModules = useMemo(() => {
    if (!isEdit) return [];
    return [...originalModules].filter((m) => !enabledModules.has(m));
  }, [originalModules, enabledModules, isEdit]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return businessTemplates.update(template!.id, {
          name,
          description: description || null,
          enabled_modules: Array.from(enabledModules),
        });
      }
      return businessTemplates.create({
        name,
        source_template_id: sourceId,
        description: description || null,
      });
    },
    onSuccess: async () => {
      // Для нових — додатково PATCH'имо enabled_modules якщо змінили
      // у формі (clone_template скопіював з джерела, користувач міг
      // зменшити). При створенні попередження не показуємо — нема
      // документів у тільки-що-склонованому шаблоні.
      onSaved();
    },
    onError: (e) => setError(humanizeBackendError(e)),
  });

  function toggleModule(code: string) {
    setEnabledModules((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function canSubmit(): boolean {
    if (!name.trim()) return false;
    if (!isEdit && !sourceId) return false;
    return true;
  }

  function handleSave() {
    setError(null);
    // Якщо редагування + звужуємо modules — показати confirmation.
    if (isEdit && removedModules.length > 0) {
      setRemovedModulesPreview(removedModules);
      setConfirmOpen(true);
      return;
    }
    saveMut.mutate();
  }

  function confirmSave() {
    setConfirmOpen(false);
    saveMut.mutate();
  }

  const isSystem = isEdit && template!.is_system;
  const title = isEdit
    ? isSystem ? `Шаблон «${template!.name}» (системний, тільки перегляд)` : `Редагування шаблону «${template!.name}»`
    : 'Створення кастомного шаблону';

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpl-editor-title"
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 900,
        }}
      >
        <div
          style={{
            background: 'var(--bg, white)',
            color: 'var(--text, #111)',
            borderRadius: 8,
            padding: '1.5rem',
            maxWidth: 720, width: '92%',
            maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
          }}
        >
          <h3 id="tpl-editor-title" style={{ marginTop: 0 }}>{title}</h3>

          {step === 1 && !isEdit && (
            <>
              <p style={{ marginTop: 0 }}>
                Виберіть шаблон-джерело. Поля (модулі, налаштування,
                іконка) буде скопійовано — потім можна редагувати.
              </p>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="source-select" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                  Шаблон-джерело
                </label>
                <select
                  id="source-select"
                  value={sourceId}
                  onChange={(e) => setSourceId(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="">— оберіть шаблон —</option>
                  <optgroup label="Системні">
                    {availableSources.filter((t) => t.is_system).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </optgroup>
                  {availableSources.some((t) => !t.is_system) && (
                    <optgroup label="Кастомні (свого tenant'а)">
                      {availableSources.filter((t) => !t.is_system).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button type="button" onClick={onClose}>Скасувати</button>
                <button
                  type="button"
                  className="primary"
                  disabled={!sourceId}
                  onClick={() => setStep(2)}
                >
                  Далі →
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ marginBottom: '0.75rem' }}>
                <label htmlFor="tpl-name" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                  Назва
                </label>
                <input
                  id="tpl-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSystem}
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label htmlFor="tpl-descr" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                  Опис
                </label>
                <textarea
                  id="tpl-descr"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isSystem}
                  rows={2}
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>

              <fieldset style={{ marginBottom: '0.75rem', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <legend style={{ padding: '0 0.5rem', fontWeight: 600 }}>
                  Активні модулі ({enabledModules.size})
                </legend>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.25rem 1rem' }}>
                  {FORM_MODULES.map((m) => (
                    <label
                      key={m}
                      style={{ display: 'block', padding: '0.15rem 0' }}
                    >
                      <input
                        type="checkbox"
                        checked={enabledModules.has(m)}
                        onChange={() => toggleModule(m)}
                        disabled={isSystem}
                        style={{ marginRight: '0.5rem' }}
                      />
                      {MODULE_NAMES_UK[m] ?? m}
                      <span style={{ color: 'var(--text-muted, #999)', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                        ({m})
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {isEdit && removedModules.length > 0 && (
                <div
                  style={{
                    background: 'var(--warning-bg, #fff8e0)',
                    color: 'var(--warning-text, #8a6a00)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--radius)',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                  }}
                >
                  Ви видаляєте модулі:{' '}
                  <strong>{removedModules.map(moduleName).join(', ')}</strong>.
                  При збереженні буде показано скільки документів стане
                  невидимими.
                </div>
              )}

              {error && (
                <div style={{ background: 'var(--error-bg, #fee)', color: 'var(--error, #c00)', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius)', marginBottom: '0.5rem', whiteSpace: 'pre-wrap' }}>
                  {error}
                </div>
              )}

              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  {!isEdit && (
                    <button type="button" onClick={() => setStep(1)}>← Назад</button>
                  )}
                </div>
                <div>
                  <button type="button" onClick={onClose} style={{ marginRight: '0.5rem' }}>
                    Закрити
                  </button>
                  {!isSystem && (
                    <button
                      type="button"
                      className="primary"
                      disabled={!canSubmit() || saveMut.isPending}
                      onClick={handleSave}
                    >
                      {saveMut.isPending ? 'Збереження…' : isEdit ? 'Зберегти' : 'Створити'}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModuleNarrowingModal
        open={confirmOpen}
        title="Звуження бізнес-шаблону"
        removedModules={removedModulesPreview}
        affected={affected}
        isLoading={affectedQuery.isLoading || affectedQuery.isFetching}
        onConfirm={confirmSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
